import { useSystemHealth } from "@src/presentation/hooks/system";
import { Spinner } from "@heroui/react";
import { AlertCircle, RefreshCw, Database } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export function ServiceHealth() {
    const { data: health, isLoading, isError, error, refetch, dataUpdatedAt } = useSystemHealth({
        refetchInterval: 30000, // Refetch every 30 seconds
    });

    // Loading state
    if (isLoading) {
        return (
            <div className="rounded border border-border bg-secondary">
                <div className="border-b border-border px-6 py-4">
                    <h2 className="text-lg font-semibold text-foreground">Service Health</h2>
                </div>
                <div className="flex justify-center py-12">
                    <div className="text-center">
                        <Spinner size="lg" color="primary" />
                        <p className="mt-4 text-sm text-foreground-500">Checking system health...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (isError || !health) {
        return (
            <div className="rounded border border-border bg-secondary">
                <div className="border-b border-border px-6 py-4">
                    <h2 className="text-lg font-semibold text-foreground">Service Health</h2>
                </div>
                <div className="p-6">
                    <div className="rounded-lg border border-danger/20 bg-danger/5 p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 shrink-0 text-danger" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-danger">Failed to load health status</p>
                                <p className="mt-1 text-xs text-danger/80">
                                    {error?.message || "Unable to retrieve system health information"}
                                </p>
                            </div>
                            <button
                                onClick={() => refetch()}
                                className="rounded p-1 hover:bg-danger/10"
                                title="Retry"
                            >
                                <RefreshCw className="h-4 w-4 text-danger" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const services = Object.entries(health.services);
    const allHealthy = health.status === "healthy";
    const healthyCount = health.getHealthyCount();
    const unhealthyCount = health.getUnhealthyCount();

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            <div className="border-b border-border px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold text-foreground">Service Health</h2>
                        <button
                            onClick={() => refetch()}
                            className="rounded p-1.5 hover:bg-muted"
                            title="Refresh health status"
                        >
                            <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${allHealthy ? "bg-success" : "bg-danger"}`} />
                        <span className={`text-sm font-medium ${allHealthy ? "text-success" : "text-danger"}`}>
                            {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                        </span>
                    </div>
                </div>

                {/* Summary stats */}
                {!allHealthy && (
                    <div className="mt-3 flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-success" />
                            <span className="text-muted-foreground">{healthyCount} Healthy</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-danger" />
                            <span className="text-muted-foreground">{unhealthyCount} Unhealthy</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {services.map(([serviceName, service]) => {
                        const isHealthy = service.status === "healthy";

                        return (
                            <div
                                key={serviceName}
                                className="flex items-start justify-between rounded-lg border border-border bg-background p-4 transition-colors hover:bg-muted/50"
                            >
                                <div className="flex items-start gap-3 flex-1">
                                    <div
                                        className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${isHealthy
                                            ? "bg-success/10 text-success"
                                            : "bg-danger/10 text-danger"
                                            }`}
                                    >
                                        {isHealthy ? (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        ) : (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-foreground capitalize">{serviceName}</p>
                                        <p className={`text-sm ${isHealthy ? "text-success" : "text-danger"}`}>
                                            {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                                        </p>
                                        {service.message && (
                                            <p className="mt-1 text-xs text-muted-foreground break-words">
                                                {service.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Database Pool Section */}
                {health.databasePool && (
                    <div className="mt-4">
                        <h3 className="mb-3 text-sm font-medium text-foreground">Database Connection Pool</h3>
                        <div className="rounded-lg border border-border bg-background p-4">
                            <div className="flex items-start gap-3">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${health.databasePool.status === "healthy" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
                                    }`}>
                                    <Database className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <p className={`text-sm font-medium ${health.databasePool.status === "healthy" ? "text-success" : "text-danger"
                                            }`}>
                                            {health.databasePool.status.charAt(0).toUpperCase() + health.databasePool.status.slice(1)}
                                        </p>
                                        <span className="text-xs font-semibold text-foreground">
                                            {health.databasePool.utilization_percent}% utilized
                                        </span>
                                    </div>
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                        <div
                                            className={`h-full transition-all ${health.databasePool.utilization_percent > 80
                                                ? "bg-danger"
                                                : health.databasePool.utilization_percent > 50
                                                    ? "bg-warning"
                                                    : "bg-success"
                                                }`}
                                            style={{ width: `${health.databasePool.utilization_percent}%` }}
                                        />
                                    </div>
                                    <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                                        <div>
                                            <p className="text-muted-foreground">Pool Size</p>
                                            <p className="font-semibold text-foreground">{health.databasePool.statistics.size}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Active</p>
                                            <p className="font-semibold text-foreground">{health.databasePool.statistics.checked_out}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Available</p>
                                            <p className="font-semibold text-foreground">{health.databasePool.statistics.checked_in}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span>
                        Last checked: {formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })}
                    </span>
                </div>
            </div>
        </div>
    );
}
